/**
 * SeverityRulesEditor — dynamic severity escalation rules.
 * Default severity + conditional overrides (IF field op value THEN severity).
 */

export interface SeverityRule {
  condition: {
    field: string;
    operator: string;
    value: any;
  };
  severity: string;
}

interface SeverityRulesEditorProps {
  defaultSeverity: string;
  rules: SeverityRule[];
  onDefaultChange: (severity: string) => void;
  onRulesChange: (rules: SeverityRule[]) => void;
  fields: string[];
  operators: string[];
  inputStyle: React.CSSProperties;
  selectStyle: React.CSSProperties;
  btnStyle: React.CSSProperties;
}

const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'var(--red)',
  HIGH: '#e65100',
  MEDIUM: 'var(--amber)',
  LOW: 'var(--green)',
};
const NO_VALUE_OPS = ['IS_NULL', 'IS_NOT_NULL'];

export function SeverityRulesEditor({
  defaultSeverity, rules, onDefaultChange, onRulesChange,
  fields, operators, inputStyle, selectStyle, btnStyle,
}: SeverityRulesEditorProps) {
  const updateRule = (idx: number, patch: Partial<SeverityRule>) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], ...patch };
    onRulesChange(updated);
  };

  const updateCondition = (idx: number, key: string, val: any) => {
    const updated = [...rules];
    updated[idx] = {
      ...updated[idx],
      condition: { ...updated[idx].condition, [key]: val },
    };
    onRulesChange(updated);
  };

  const addRule = () => {
    onRulesChange([
      ...rules,
      { condition: { field: 'amount', operator: 'GT', value: 0 }, severity: 'HIGH' },
    ]);
  };

  const removeRule = (idx: number) => {
    onRulesChange(rules.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {/* Default severity */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>
          Default Severity
        </label>
        <select value={defaultSeverity} onChange={e => onDefaultChange(e.target.value)} style={selectStyle}>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Escalation rules */}
      {rules.length > 0 && (
        <div style={{
          border: '1px solid var(--border-default)',
          borderRadius: 6,
          padding: 12,
          marginBottom: 12,
        }}>
          <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: 10, fontWeight: 600 }}>
            Escalation Rules
          </p>
          {rules.map((rule, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', minWidth: 16 }}>
                IF
              </span>
              <select
                value={rule.condition.field || ''}
                onChange={e => updateCondition(idx, 'field', e.target.value)}
                style={{ ...selectStyle, width: 140 }}
              >
                {fields.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <select
                value={rule.condition.operator || ''}
                onChange={e => updateCondition(idx, 'operator', e.target.value)}
                style={{ ...selectStyle, width: 120 }}
              >
                {operators.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {!NO_VALUE_OPS.includes(rule.condition.operator) && (
                <input
                  value={rule.condition.value ?? ''}
                  onChange={e => {
                    const v = e.target.value;
                    const num = Number(v);
                    updateCondition(idx, 'value', isNaN(num) || v === '' ? v : num);
                  }}
                  style={{ ...inputStyle, width: 120 }}
                  placeholder="value"
                />
              )}
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600 }}>
                THEN &rarr;
              </span>
              <select
                value={rule.severity}
                onChange={e => updateRule(idx, { severity: e.target.value })}
                style={{
                  ...selectStyle,
                  width: 110,
                  color: SEVERITY_COLORS[rule.severity] || 'inherit',
                  fontWeight: 600,
                }}
              >
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => removeRule(idx)}
                style={{ ...btnStyle, padding: '3px 8px', color: 'var(--red)' }}
              >
                X
              </button>
            </div>
          ))}
        </div>
      )}

      <button onClick={addRule} style={{ ...btnStyle, fontSize: 11 }}>
        + Add Escalation Rule
      </button>
    </div>
  );
}
